import { useReducedMotion } from '@react-spring/web';
import React from 'react';
import { default as ReactMarquee } from 'react-fast-marquee';
import { Typography } from 'src/components-v2/DataDisplay';
import HideFromUser from 'src/components-v2/Helpers/HideFromUser/HideFromUser';
import { Box, Column, Row, Stack } from 'src/components-v2/Layout';
import { Image } from 'src/components-v2/Media';
import { isAutomatedBrowser } from 'src/lib/browser-utils';
import { useLazyGuilloche } from 'src/hooks/useLazyGuilloche';
import useWorkerGuilloche from 'src/hooks/useWorkerGuilloche';
import styled from 'threads5/styles/styled';
import SxProps from 'threads5/themes/sx-props';
import type { ITheme } from 'threads5/themes/theme';
import CountingNumber, { Number } from './CountingNumber';
import FloatingMoment, { MomentProps } from './FloatingMoment';
import NotificationMoment from './NotificationMoment';

const Heading2 = styled('h2')(({ theme }) => {
  return {
    fontFamily: theme.typography.plaidSans,
    color: '#02294B',
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    fontSize: '3rem',
    lineHeight: '120%',
    letterSpacing: '-1.6px',
    fontWeight: 500,

    [theme.breakpoints.up('sm')]: {
      fontSize: '5rem',
      lineHeight: '110%',
      letterSpacing: '-2px',
      paddingRight: '5rem',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '7rem',
      lineHeight: '120%',
      letterSpacing: '-2.8px',
      paddingRight: 0,
    },
  };
});

const Heading3 = styled('span')(({ theme }) => {
  return {
    display: 'block',
    fontFamily: theme.typography.plaidSans,
    color: '#02294B',
    lineHeight: '94%',
    fontSize: '1.8rem',
    fontWeight: 500,
    letterSpacing: '-0.72px',
    textWrap: 'balance',
    [theme.breakpoints.up('sm')]: {
      fontSize: '3.5rem',
      fontWeight: 400,
      letterSpacing: '-1.4px',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '3rem',
      fontWeight: 500,
      letterSpacing: '-1.2px',
      marginTop: '14px',
    },
  };
});

const ImageMoment = ({ src, alt }: { src: string; alt: string }) => {
  return (
    <Image
      src={src}
      width='238px'
      height='83px'
      alt={alt}
      isLazy={true}
      sx={{
        width: { xs: '165.6px', md: '238px' },
        height: { xs: '65px', md: '83px' },
        objectFit: 'cover',
        borderRadius: '13px',
        boxShadow:
          '0px 86.756px 24.378px 0px rgba(125, 125, 125, 0.00), 0px 55.209px 22.227px 0px rgba(125, 125, 125, 0.01), 0px 30.831px 18.642px 0px rgba(125, 125, 125, 0.05), 0px 13.623px 13.623px 0px rgba(125, 125, 125, 0.09), 0px 3.585px 7.887px 0px rgba(125, 125, 125, 0.10)',
      }}
    />
  );
};

const Moment1 = ({ sx }: MomentProps) => {
  return (
    <FloatingMoment
      sx={{
        top: 'min(15vh, 180px)',
        marginLeft: 'min(4vw, 8%)',
        ...sx,
      }}
    >
      <ImageMoment
        src='https://images.ctfassets.net/ss5kfr270og3/10W4pmTmGDKRBlPeE7ZLHo/df2a1d7f73f3828bc00aabacb4e43c17/moment1.png?fm=webp&q=70&w=576&h=166'
        alt='moment1'
      />
    </FloatingMoment>
  );
};

const VenmoMoment = ({ sx }: MomentProps) => {
  return (
    <FloatingMoment
      sx={{
        top: 'min(32vh, 384px)',
        marginLeft: 'min(10vw, 20%)',
        ...sx,
      }}
    >
      <NotificationMoment
        icon='https://images.ctfassets.net/ss5kfr270og3/YSwn9nM03H7zyUErHpXsS/dce654f0e07491434ff9d00ca469b7e1/Venmo.png?fm=webp&q=70&w=254&h=254'
        iconAlt='venmo'
        title='for late night 🌮🌮🌮'
        description='Venmo'
        time='2m ago'
      />
    </FloatingMoment>
  );
};

const Moment2 = ({ sx }: MomentProps) => {
  return (
    <FloatingMoment
      sx={{
        top: 'min(49vh, 588px)',
        marginLeft: 'min(15vw, 30%)',
        ...sx,
      }}
    >
      <ImageMoment
        src='https://images.ctfassets.net/ss5kfr270og3/5lMPORJ35CEi9ZkTClfmuC/6c4596a8633c09a71992fe99677459fe/moment2.png?fm=webp&q=100&w=576&h=166'
        alt='moment2'
      />
    </FloatingMoment>
  );
};

const CarvanaMoment = ({ sx }: MomentProps) => {
  return (
    <FloatingMoment
      sx={{
        top: 'min(66vh, 792px)',
        marginLeft: 'min(12vw, 24%)',
        ...sx,
      }}
    >
      <NotificationMoment
        icon='https://images.ctfassets.net/ss5kfr270og3/3dwPjiWbz2EpbdhyDRP8Mn/3b4042e51a07b06031e6b0a9ddb786a9/Carvana.png?fm=webp&q=70&w=254&h=254'
        iconAlt='carvana'
        title="You're pre-qualified!"
        description='Carvana'
        time='1hr ago'
      />
    </FloatingMoment>
  );
};

const Moment3 = ({ sx }: MomentProps) => {
  return (
    <FloatingMoment
      sx={{
        top: 'min(83vh, 996px)',
        marginLeft: 'min(8vw, 16%)',
        ...sx,
      }}
    >
      <ImageMoment
        src='https://images.ctfassets.net/ss5kfr270og3/4ktxRP6n3X4olOZvTY6CQ8/30ec724f3031515c1f3fd1bc0df36776/moment3.png?fm=webp&q=70&w=576&h=166'
        alt='moment3'
      />
    </FloatingMoment>
  );
};

const CitiMoment = ({ sx }: MomentProps) => {
  return (
    <FloatingMoment
      sx={{
        top: 'min(100vh, 1200px)',
        marginLeft: 'min(12vw, 24%)',
        ...sx,
      }}
    >
      <NotificationMoment
        icon='https://images.ctfassets.net/ss5kfr270og3/73s1nZJxfHSosVSa3RBiLj/02ee1c52a75be757b7a1fdd3ee13db5d/Banks-US.png?fm=webp&q=70&w=254&h=254'
        iconAlt='citibank'
        title='Checking account linked'
        description='Citi'
        time='7m ago'
      />
    </FloatingMoment>
  );
};

const RobinhoodMoment = ({ sx }: MomentProps) => {
  return (
    <FloatingMoment
      sx={{
        top: 'min(100vh, 1200px)',
        marginLeft: 'min(12vw, 24%)',
        ...sx,
      }}
    >
      <NotificationMoment
        icon='https://images.ctfassets.net/ss5kfr270og3/6ffFslxGF555jjvupDmzhv/0e2b5c9f2c1120b324ddae84bf04e34a/Robinhood.png?fm=webp&q=70&w=254&h=254'
        iconAlt='robinhood'
        title='$PLAID order filled'
        description='Robinhood'
        time='Now'
      />
    </FloatingMoment>
  );
};

const Moment4 = ({ sx }: MomentProps) => {
  return (
    <FloatingMoment
      sx={{
        top: 'min(117vh, 1404px)',
        marginLeft: 'min(2vw, 4%)',
        ...sx,
      }}
    >
      <ImageMoment
        src='https://images.ctfassets.net/ss5kfr270og3/1uyEDzWCa5DL0QjnmHoZIZ/0979df622ebe4861ce846f228c935878/moment4.png?fm=webp&q=90&w=476&h=166'
        alt='moment4'
      />
    </FloatingMoment>
  );
};

const ChimeMoment = ({ sx }: MomentProps) => {
  return (
    <FloatingMoment
      sx={{
        top: 'min(100vh, 1200px)',
        marginLeft: 'min(12vw, 24%)',
        ...sx,
      }}
    >
      <NotificationMoment
        icon='https://images.ctfassets.net/ss5kfr270og3/7wxAvraQxeuegyvgIL6w81/15aa5d904e1ef0c9696eeab4ca3289c2/Chime.png?fm=webp&q=70&w=254&h=254'
        iconAlt='chime logo'
        title='Your account is funded'
        description='Chime'
        time='2m ago'
      />
    </FloatingMoment>
  );
};

const MobileMoments = () => {
  const prefersReducedMotion = useReducedMotion();
  // useReducedMotion is null on first render; the sync check stops captures
  // from freezing the marquees mid-scroll.
  const marqueeSpeed = prefersReducedMotion || isAutomatedBrowser() ? 0 : 25;
  const mobileStyles = {
    position: 'relative',
    top: 'unset',
    marginLeft: 'unset',
    display: {
      xs: 'block',
    },
    minHeight: '55px',
  };

  return (
    <Stack
      className='hide-for-aeo'
      gap={{
        xs: 5,
        md: 11,
      }}
      sx={{
        marginTop: { xs: '10rem', sm: 0 },
        display: {
          xs: 'flex',
          sm: 'flex',
          md: 'flex',
          lg: 'none',
        },
        width: '100%',
      }}
    >
      <ReactMarquee
        pauseOnHover={false}
        pauseOnClick={false}
        gradient={false}
        key='logo-marquee-1'
        speed={marqueeSpeed}
        style={{
          overflowX: 'unset',
        }}
      >
        <Stack
          direction='row'
          sx={{
            gap: '50px',
            alignItems: 'center',
            ':last-child': {
              marginRight: '50px',
            },
          }}
        >
          <RobinhoodMoment sx={mobileStyles} />
          <Moment1 sx={mobileStyles} />
          <VenmoMoment sx={mobileStyles} />
          <Moment2 sx={mobileStyles} />
          <CitiMoment sx={mobileStyles} />
          <Moment3 sx={mobileStyles} />
          <CarvanaMoment sx={mobileStyles} />
          <Moment4 sx={mobileStyles} />
          <ChimeMoment sx={mobileStyles} />
        </Stack>
      </ReactMarquee>
      <ReactMarquee
        pauseOnHover={false}
        pauseOnClick={false}
        gradient={false}
        key='logo-marquee-2'
        speed={marqueeSpeed}
        style={{
          overflowX: 'unset',
        }}
        direction='right'
      >
        <Stack
          gap={6}
          direction='row'
          sx={{
            gap: '50px',
            alignItems: 'center',
            ':last-child': {
              marginRight: '50px',
            },
          }}
        >
          <CitiMoment sx={mobileStyles} />
          <Moment2 sx={mobileStyles} />
          <VenmoMoment sx={mobileStyles} />
          <Moment1 sx={mobileStyles} />
          <RobinhoodMoment sx={mobileStyles} />
          <ChimeMoment sx={mobileStyles} />
          <Moment4 sx={mobileStyles} />
          <CarvanaMoment sx={mobileStyles} />
          <Moment3 sx={mobileStyles} />
        </Stack>
      </ReactMarquee>

      <ReactMarquee
        pauseOnHover={false}
        pauseOnClick={false}
        gradient={false}
        key='logo-marquee-3'
        speed={marqueeSpeed}
        style={{
          overflowX: 'unset',
        }}
      >
        <Stack
          gap={6}
          direction='row'
          sx={{
            gap: '50px',
            alignItems: 'center',
            ':last-child': {
              marginRight: '50px',
            },
          }}
        >
          <CarvanaMoment sx={mobileStyles} />
          <Moment4 sx={mobileStyles} />
          <ChimeMoment sx={mobileStyles} />
          <RobinhoodMoment sx={mobileStyles} />
          <Moment1 sx={mobileStyles} />
          <VenmoMoment sx={mobileStyles} />
          <Moment2 sx={mobileStyles} />
          <CitiMoment sx={mobileStyles} />
          <Moment3 sx={mobileStyles} />
        </Stack>
      </ReactMarquee>
    </Stack>
  );
};

const StatRow = ({
  children,
  sx,
}: {
  children: React.ReactNode;
  sx?: SxProps<ITheme>;
}) => {
  return (
    <Row
      columns={{
        xs: 19,
        lg: 10,
      }}
      sx={sx}
    >
      {children}
    </Row>
  );
};

const GuillocheCanvas = styled('canvas')(({ theme }) => {
  return {
    paddingRight: '6rem',
    position: 'relative',
    width: '100%',
    height: '100%',
    background: 'transparent',
    pointerEvents: 'none',
    [theme.breakpoints.up('lg')]: {
      top: 'max(-80vh, -960px)',
      left: 'max(-13vw, -26%)',
      marginRight: 'min(40vw, 80%)',
      height: 'min(250vh, 3000px)',
    },
    [theme.breakpoints.up(1731)]: {
      maskImage:
        'linear-gradient(to right, transparent 0, black 80px, black calc(100% - 80px), transparent 100%)',
    },
    [theme.breakpoints.only('md')]: {
      top: '-10vh',
      left: '-28vw',
    },
    [theme.breakpoints.down('md')]: {
      top: '5vh',
      left: '-25vw',
    },
    [theme.breakpoints.only('xs')]: {
      top: '-5vh',
      left: '-35vw',
    },
  };
});

const Guilloche = () => {
  const guillocheRef = React.useRef<HTMLCanvasElement>(null);
  const { guilloche: ribbon2, isGuillocheLoaded: isRibbon2Loaded } =
    useLazyGuilloche({
      pattern: 'donut',
      canvasRef: guillocheRef,
    });
  useWorkerGuilloche({
    canvasRef: guillocheRef,
    svgPaths: ribbon2?.paths,
    svgWidth: ribbon2?.width,
    svgHeight: ribbon2?.height,
    intensity: 1,
    speed: 0.27,
    horizontalWave: false,
    verticalWave: true,
    zAxisWave: true,
    id: 'network-canvas',
    scale: { xs: 1, sm: 2.3, xxl: 2 },
    x: { xs: -45, sm: -50, xxl: -30 },
    y: { sm: 0, md: 10 },
    disableAnimation: !isRibbon2Loaded,
  });

  return <GuillocheCanvas ref={guillocheRef} />;
};

const NetworkSection: React.FC<{
  isGuillocheDisabled: boolean;
}> = ({ isGuillocheDisabled }) => {
  return (
    <Box
      sx={{
        position: 'relative',
        overflowX: 'clip',
      }}
    >
      <Row
        sx={{
          maxWidth: '1730px',
          margin: '0 auto',
          height: {
            xs: '1000px',
            sm: '1500px',
            lg: '1800px',
            xl: '2000px',
          },
          paddingBottom: {
            xs: '24px',
            lg: 0,
          },
          minHeight: 'fit-content',
          display: 'flex',
          zIndex: 1,
          position: 'relative',
          overflow: 'visible',
        }}
      >
        <Column
          className='hide-for-aeo'
          sx={{
            height: '100%',
            position: {
              xs: 'absolute',
              lg: 'relative',
            },
            zIndex: 0,
          }}
          xs={24}
          sm={24}
          lg={12}
        >
          {!isGuillocheDisabled && <Guilloche />}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '100%',
              height: '100%',
              display: 'inline-block',
            }}
          >
            <Moment1 />
            <VenmoMoment />
            <Moment2 />
            <CarvanaMoment />
            <Moment3 />
            <RobinhoodMoment />
            <Moment4 />
          </Box>
        </Column>
        <Column
          xsOffset={2}
          xs={19}
          lgOffset={0}
          lg={10}
          sx={{
            zIndex: 1,
            paddingTop: {
              xs: '130px',
              sm: '100px',
              lg: '180px',
              xl: '250px',
            },
          }}
        >
          <Heading2>A network that makes your products better</Heading2>
          <Typography
            variant='P1 Regular'
            component='p'
            sx={{
              textAlign: 'left',
              marginTop: '24px',
              fontSize: {
                xs: '1.8rem',
                sm: '2rem',
                md: '2.4rem',
              },
              paddingRight: {
                lg: '10rem',
              },
            }}
          >
            When you build on the world's largest financial data network, your
            products get smarter with every connection&mdash;helping you
            increase conversion, expand access, and grow faster.
          </Typography>
          <StatRow
            sx={{
              marginTop: {
                xs: '6.4rem',
                sm: '9.5rem',
                lg: '14rem',
              },
            }}
          >
            <Column xs={24} sm={8} lg={4}>
              <h3>
                <Number className='hide-for-aeo'>1 in 2</Number>
                {/* We add this node to make markdown compilation work make sure to update both the <Number> and <span> */}
                <HideFromUser>1 in 2&nbsp;</HideFromUser>
                <br className='hide-for-aeo' />
                <Heading3>banked adults in the U.S. use Plaid</Heading3>
              </h3>
            </Column>
            <Column
              xs={24}
              smOffset={3}
              sm={8}
              lgOffset={1}
              lg={4}
              sx={{ marginTop: { xs: '3rem', sm: 0 } }}
            >
              <h3>
                <CountingNumber
                  className='hide-for-aeo'
                  number={25}
                  suffix='%'
                  speedMs={40}
                />
                {/* We add this node to make markdown compilation work make sure to update both the <Number> and <span> */}
                <HideFromUser>25%&nbsp;</HideFromUser>
                <br className='hide-for-aeo' />
                <Heading3>higher onboarding conversion</Heading3>
              </h3>
            </Column>
          </StatRow>
          <StatRow
            sx={{
              marginTop: {
                xs: '3rem',
                sm: '7rem',
              },
              marginBottom: {
                xs: '30px',
                sm: '130px',
                lg: '140px',
              },
            }}
          >
            <Column xs={24} sm={8} lg={4}>
              <h3>
                <Number className='hide-for-aeo'>1M+</Number>
                {/* We add this node to make markdown compilation work make sure to update both the <Number> and <span> */}
                <HideFromUser>Over one million&nbsp;</HideFromUser>
                <br className='hide-for-aeo' />
                <Heading3>daily connections</Heading3>
              </h3>
            </Column>
            <Column
              xs={24}
              smOffset={3}
              sm={8}
              lgOffset={1}
              lg={4}
              sx={{ marginTop: { xs: '3rem', sm: 0 } }}
            >
              <h3>
                <CountingNumber
                  className='hide-for-aeo'
                  number={12_000}
                  speedMs={40}
                  startNumber={5_000}
                />
                {/* We add this node to make markdown compilation work make sure to update both the <Number> and <span> */}
                <HideFromUser>12,000&nbsp;</HideFromUser>
                <br className='hide-for-aeo' />
                <Heading3>financial institutions across 20 countries</Heading3>
              </h3>
            </Column>
          </StatRow>
        </Column>
        <MobileMoments />
      </Row>
    </Box>
  );
};

export default NetworkSection;
